import React, { ReactNode, useEffect } from 'react';
import { connect } from 'react-redux';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { Spinner } from '@tink/core/src/components/atoms/Spinner/Spinner';
import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import { PageView } from '../../../models/analytics';
import { AppState, ConnectedPropsWithoutDispatch } from '../../../stores/redux-store';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { Header } from '../../../components/organisms/Header/Header';
import { ProviderEntity } from '../../../models/provider';
import { AwaitingScreenHeader } from '../../../components/screens/AwaitingCredentialsScreen/organisms/molecules/AwaitingScreenHeader/AwaitingScreenHeader';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { InstructionListBody } from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/molecules/InstructionList/InstructionListBody';
import { InstructionListHeader } from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/molecules/InstructionList/InstructionListHeader';
import { ApplicationUnverifiedLabel } from '../../../components/molecules/ApplicationUnverifiedLabel/ApplicationUnverifiedLabel';
import { Stack } from '../../../components/atoms/Stack/Stack';
import { BackButton } from '../../../components/organisms/Header/HeaderMolecule';

export type ProviderRedirectInstructionsOwnProps = {
  provider: ProviderEntity;
  pageView: PageView;
  userHintId?: string;
  primaryButton: ReactNode;
  instructionsTitle: ReactNode;
  instructions?: ReactNode[];
  legalDisclaimer?: ReactNode;
  body?: ReactNode;
  footer?: ReactNode;
  hideHeader?: boolean;
  allowExit?: boolean;
} & BackButton;

export type ProviderRedirectInstructionsProps = PropsFromRedux &
  ProviderRedirectInstructionsOwnProps;

export const ProviderRedirectInstructionsPresenter: React.FC<ProviderRedirectInstructionsProps> = ({
  provider,
  pageView,
  allowExit = true,
  body,
  footer,
  userHintId,
  primaryButton,
  legalDisclaimer,
  instructions,
  isClientVerified,
  instructionsTitle,
  onBack,
  isCredentialsLoading,
  hideHeader = false,
}) => {
  const { tinklinkAnalytics } = useAnalytics();

  useEffect(() => {
    tinklinkAnalytics.sendViewEvent({ view: pageView });
  }, [pageView, tinklinkAnalytics]);

  const hasInstructionSteps = Boolean(instructions?.length);
  const shouldCenterContent = !hasInstructionSteps;

  return (
    <ScreenWrapper data-testid="provider-redirect-instructions">
      {!hideHeader &&
        (onBack ? (
          <Header onBack={onBack} pageView={pageView} />
        ) : (
          <AwaitingScreenHeader pageView={pageView} onBack={undefined} allowExit={allowExit} />
        ))}
      <ContentBody>
        <Stack justifyContent={shouldCenterContent ? 'center' : 'normal'}>
          <InstructionListHeader
            title={instructionsTitle}
            provider={provider}
            userHintId={userHintId}
          />
          {hasInstructionSteps && instructions && (
            <InstructionListBody instructions={instructions} />
          )}
          {body}
          <ApplicationUnverifiedLabel isVerified={isClientVerified} />
        </Stack>
        {footer && (
          <Box mb="normal" fullWidth>
            {footer}
          </Box>
        )}
        {(isCredentialsLoading || !primaryButton) && (
          <Box textAlign="center" mb="small" lineHeight={1}>
            <Spinner size={48} />
          </Box>
        )}
        {!isCredentialsLoading && primaryButton && (
          <Box textAlign="center" mb="medium">
            {primaryButton}
          </Box>
        )}
      </ContentBody>
      {legalDisclaimer}
    </ScreenWrapper>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    isClientVerified: Boolean(state.oauth.description?.verified),
    isCredentialsLoading: state.credentials.loading,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedPropsWithoutDispatch<typeof connector>;

export const ProviderRedirectInstructions = connector(ProviderRedirectInstructionsPresenter);
